import { FormattedMessage, useIntl } from 'react-intl';
import Input, { InputWithMenu } from '../Input';
import { useFormContext, useWatch } from 'react-hook-form';
import { OrderSendForm } from '@/types/order';
import { useEffect, useState } from 'react';
import useLocale from '@/hooks/useLocale';
import { countriesEnData } from '@/utils/countriesEnData';
import { countriesChData } from '@/utils/countriesChData';
import { countriesJaData } from '@/utils/countriesJaData';
import { countriesEsData } from '@/utils/countriesEsData';
import { useShippingCountries } from '@/hooks/useShippingCountries';
import { Image } from '@/components/Image';

const emailDomainData = ['gmail.com', 'hotmail.com', 'yahoo.com', 'icloud.com', 'Enter manually'];

const emailDomainRegexp = /^([\w-]+\.)+[\w-]{2,4}$/;
// 허용 문자 집합 (입력 단계 제한용)
const emailLocalAllowedChar = /^[A-Za-z0-9._%+-]$/; // 로컬 파트에서 허용되는 단일 문자
const sanitizeEmailLocal = (value: string) => value.replace(/[^A-Za-z0-9._%+-]/g, '');

export const CustomerInfo = ({ isGlobal }: { isGlobal: boolean }) => {
  const intl = useIntl();
  const { register, setValue } = useFormContext<OrderSendForm>();
  const [isOptionVisible, setIsOptionVisible] = useState(false);
  const [emailDomainErrorMessage, setEmailDomainErrorMessage] = useState<string | undefined>();
  const countryValue = useWatch({ name: 'customer_country_code' });
  const domainValue = useWatch({ name: 'customer_email_2' });
  const { locale } = useLocale();
  const [countriesDataArr, setCountriesDataArr] = useState(countriesEnData);
  const { countries: shippingCountries } = useShippingCountries();

  useEffect(() => {
    switch (locale) {
      case 'en':
        return setCountriesDataArr(countriesEnData);
      case 'zh':
        return setCountriesDataArr(countriesChData);
      case 'ja':
        return setCountriesDataArr(countriesJaData);
      case 'es':
        return setCountriesDataArr(countriesEsData);
    }
  }, [locale]);

  useEffect(() => {
    setEmailDomainErrorMessage(
      emailDomainRegexp.test(domainValue) || domainValue === undefined ? '' : 'Please enter a correct email address.'
    );
  }, [domainValue]);

  useEffect(() => {
    setValue('customer_email_2', 'gmail.com');
  }, []);

  const handleEmailDomainClick = (emailDomain: string) => {
    if (emailDomain === 'Enter manually') {
      setValue('customer_email_2', '');
    } else {
      setValue('customer_email_2', emailDomain);
    }
  };

  // 이메일 입력 단계에서 허용되지 않는 문자 차단 (로컬 파트)
  const handleEmailLocalBeforeInput: React.FormEventHandler<HTMLInputElement> = (e) => {
    // @ts-expect-error: BeforeInputEvent의 data 속성 접근
    const data: string | null = e.nativeEvent?.data ?? null;
    if (!data) return;
    if (!emailLocalAllowedChar.test(data)) {
      e.preventDefault();
    }
  };

  const handleEmailLocalPaste: React.ClipboardEventHandler<HTMLInputElement> = (e) => {
    const pasted = e.clipboardData.getData('text');
    const sanitized = sanitizeEmailLocal(pasted);
    if (sanitized !== pasted) {
      e.preventDefault();
      setValue('customer_email_1', sanitized, { shouldValidate: true, shouldDirty: true });
    }
  };

  return (
    <>
      <div className="flex flex-col gap-6 px-5 pb-10">
        <div className="w-full flex justify-between items-center pt-10 cursor-pointer">
          <span className="text-[24px] leading-8 font-bold">
            <FormattedMessage id="order_section1_title" defaultMessage="Customer information" />
          </span>
        </div>
        <div className="flex flex-row gap-4 w-full">
          {/* First name */}
          <Input
            label={intl.formatMessage({ id: 'order_section1_input1_label', defaultMessage: 'First name' })}
            containerClassName="flex-1"
            placeholder={intl.formatMessage({ id: 'order_section1_input1_placeholder', defaultMessage: 'First name' })}
            register={register('customer_first_name', { required: true })}
          />
          {/* Last name */}
          <Input
            label={intl.formatMessage({ id: 'order_section1_input2_label', defaultMessage: 'Last name' })}
            containerClassName="flex-1"
            placeholder={intl.formatMessage({ id: 'order_section1_input2_placeholder', defaultMessage: 'Last name' })}
            register={register('customer_last_name', { required: true })}
          />
        </div>
        {/* Phone number */}
        <InputWithMenu
          label={intl.formatMessage({ id: 'order_section1_input3_label', defaultMessage: 'Phone number' })}
          //Phone number
          menus={isGlobal ? shippingCountries : countriesDataArr}
          inputContainerClassName="w-[533px]"
          placeholder={intl.formatMessage({ id: 'order_section1_input3_input-placeholder', defaultMessage: 'Phone number' })}
          register={register('customer_phone_number', { required: true })}
          menuValue={countryValue}
          onMenuSelect={(code, codeNumber) => {
            setValue('customer_country_code', code);
            setValue('customer_country_code_number', codeNumber);
          }}
          type={'number'}
        />

        {/* E-mail */}
        <div className="flex items-end relative">
          <Input
            label={
              <div className="flex items-center gap-2 relative !w-[calc(100vw-611px)]">
                {intl.formatMessage({ id: 'order_section1_input4_label', defaultMessage: 'E-mail' })}
                <div className="text-[13px] font-semibold text-[#808080]">
                  (
                  <FormattedMessage
                    id="email_input_desc"
                    defaultMessage="We will send an invoice for pick-up to the email address you provided"
                  />
                  )
                </div>
              </div>
            }
            containerClassName={`w-[320px]`}
            placeholder={'E-mail'}
            register={register('customer_email_1', {
              required: true,
              pattern: /^[A-Za-z0-9._%+-]+$/
            })}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            onBeforeInput={handleEmailLocalBeforeInput}
            onPaste={handleEmailLocalPaste}
          />
          <div className="h-[52px] flex items-center px-4 text-[14px] font-semibold">@</div>
          <Input
            containerClassName={`w-[248px] h-[52px]`}
            errorMessage={emailDomainErrorMessage}
            register={register('customer_email_2')}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
          />
          <div
            className="px-5 flex items-center justify-between border border-[#dadada] rounded-[10px] w-[248px] h-[52px] gap-8 cursor-pointer ml-2"
            onClick={() => setIsOptionVisible((pev) => !pev)}
          >
            <div>
              <span className="text-[14px] font-semibold">
                {emailDomainData.includes(domainValue) ? (
                  domainValue
                ) : (
                  <FormattedMessage id="email_domain_enter_manually" defaultMessage="Enter Manually" />
                )}
              </span>
              {isOptionVisible && (
                <ul className="w-[248px] h-[266px] absolute top-[100px] right-0 z-10 border border-[#dadada] overflow-y-scroll rounded-[10px] shadow-[0px_4px_10px_0px_#00000029] bg-white">
                  {emailDomainData.map((emailDomain) => (
                    <li
                      key={emailDomain}
                      className="bg-white text-black p-4 text-[14px] font-semibold cursor-pointer transition ease-in-out duration-300 hover:bg-[#c1c1c1]"
                      onClick={() => handleEmailDomainClick(emailDomain)}
                    >
                      {emailDomain === 'Enter manually' ? (
                        <FormattedMessage id="email_domain_enter_manually" defaultMessage="Enter Manually" />
                      ) : (
                        emailDomain
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <Image
              src="/icons/arrow-down.svg"
              alt="arrow icon"
              className="w-[15px] h-[15px] transition ease-in-out duration-300 cursor-pointer"
              style={{ transform: isOptionVisible ? 'rotate(180deg)' : 'rotate(0)' }}
              width={15}
              height={15}
            />
          </div>
        </div>
      </div>

      <div className="w-full h-4 bg-[#f5f5f5]" />
    </>
  );
};
